/**
 * Dashboard Create Route ("Choose Client" screen)
 *
 * Route: /dashboard/new
 * Search params: ?clientId=<id>
 *
 * Replaces the old `ClientPickerDialog` modal on the dashboard hub. This is a full
 * SCREEN that visually MIRRORS the report generate flow's first ("Choose Client")
 * step: a centered heading + subtext, a client selector dropdown, an animated
 * forking connector "bus", and three method cards.
 *
 * Cards (dashboard wording):
 *   - "Start from Scratch"        → POST an empty dashboard → /dashboard/$id/edit
 *   - "Generate Smart Dashboard"  → build a widget set from the client's connected
 *                                   platforms (Full Marketing template filtered via
 *                                   `filterWidgetsByAvailablePlatforms`) → POST →
 *                                   /dashboard/$id/edit
 *   - "Create a Dashboard with AI" → Coming soon (disabled)
 *
 * 🔴 The report editor files (routes/reports/**, components/reports/**, the listed
 * hooks/libs) are FROZEN. The components that are LOCAL to generate.tsx — the
 * client selector popover, the connector bus, and the method-card markup — are
 * CLONED here (with dashboard wording), NOT imported. Only genuinely shared,
 * non-frozen pieces are imported (templateUtils, useReportTemplates,
 * useClientIntegrations, ReportBreadcrumb, PICKER_PLATFORMS/logoUrl, ClientAvatar).
 */

import { useState, useCallback, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  MagnifyingGlass,
  Users,
  Check,
  CaretDown,
  FileDashedIcon,
  MagicWandIcon,
  BrainIcon,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

import { Button, Popover, PopoverTrigger, PopoverContent } from "@provenleap/ui";
import { apiGet, apiPost } from "../../../lib/apiClient";
import { useReportTemplates } from "../../../hooks/useReportTemplates";
import { useClientIntegrations } from "../../../hooks/useClientIntegrations";
import { useBrand } from "../../../hooks/useBrands";
import { filterWidgetsByAvailablePlatforms } from "../../../lib/templateUtils";
import { toCanvasLayout } from "../../../lib/dashboardLayout";
import { PICKER_PLATFORMS, logoUrl } from "../../../lib/integrations";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { ClientAvatar } from "../../../components/clients/ClientAvatar";
import { RollupSetupStep } from "../../../components/reports/RollupSetupStep";
import { ROLLUP_MEMBER_CAP } from "../../../components/reports/rollupPaletteGate";
import { useToast } from "../../../hooks/useToast";
import { Toast } from "../../../components/clients/Toast";

export interface DashboardNewSearchParams {
  clientId?: string;
  /**
   * Story 40-7 — roll-up wizard selection, carried in the URL (deep-link safe).
   * `kind` ("client"|"rollup") gates the whole roll-up branch; absent ⇒ the
   * existing single-client "Choose Client" flow (byte-identical). `rollupScope` +
   * the member set (`brandId` for brand scope, comma-joined `members` for
   * portfolio) are only meaningful when kind === "rollup".
   */
  kind?: "client" | "rollup";
  rollupScope?: "brand" | "portfolio";
  brandId?: string;
  /** Comma-joined portfolio member client ids (bounded ≤ ROLLUP_MEMBER_CAP). */
  members?: string;
}

export const Route = createFileRoute("/_authenticated/dashboard/new")({
  validateSearch: (search: Record<string, unknown>): DashboardNewSearchParams => ({
    clientId: typeof search.clientId === "string" ? search.clientId : undefined,
    kind:
      search.kind === "client" || search.kind === "rollup"
        ? search.kind
        : undefined,
    rollupScope:
      search.rollupScope === "brand" || search.rollupScope === "portfolio"
        ? search.rollupScope
        : undefined,
    brandId: typeof search.brandId === "string" ? search.brandId : undefined,
    members: typeof search.members === "string" ? search.members : undefined,
  }),
  component: DashboardNewPage,
});

/** Parse the comma-joined portfolio member param into a bounded, de-duplicated
 *  id list (mirrors the report wizard's parseMemberIds). */
function parseMemberIds(members: string | undefined): string[] {
  if (!members) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of members.split(",")) {
    const trimmed = id.trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      out.push(trimmed);
    }
  }
  return out;
}

// The same Full Marketing template the report Smart-flow uses as its widget seed.
const FULL_MARKETING_TEMPLATE_ID = "00000000-0000-0000-0000-template00006";

interface Client {
  id: string;
  name: string;
  logoUrl: string | null;
  isArchived: number;
  platforms?: string[];
}

// ── Client Selector Popover (cloned from generate.tsx, dashboard namespace) ───

function ClientSelectorPopover({
  selectedClientId,
  selectedClient,
  onSelectClient,
}: {
  selectedClientId?: string;
  selectedClient?: { id: string; name: string; logoUrl: string | null } | null;
  onSelectClient: (clientId: string) => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState("");

  const handleOpenChange = (v: boolean) => {
    if (v) {
      setOpen(true);
      setVisible(true);
    } else {
      setVisible(false);
    }
  };
  const handleExitComplete = () => {
    if (!visible) {
      setOpen(false);
      setSearch("");
    }
  };

  const {
    data: clientsData,
    isLoading,
    error,
    refetch,
  } = useQuery<Client[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) throw new Error("Failed to fetch clients");
      const json = await res.json();
      return json.data as Client[];
    },
  });

  const clients = (clientsData ?? []).filter((c) => !c.isArchived);
  const filtered = search
    ? clients.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
    : clients;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-2.5 rounded-2xl h-11 px-5 min-w-[400px] text-sm bg-card hover:bg-card hover:-translate-y-0.5 hover:shadow-sm transition-all cursor-pointer shadow-xs"
          style={{ border: "0.5px solid var(--hairline)" }}
          data-testid="client-selector-trigger"
        >
          {selectedClient ? (
            <>
              <ClientAvatar name={selectedClient.name} logoUrl={selectedClient.logoUrl ?? null} size="sm" />
              <span className="flex-1 text-left font-normal text-[14px] text-foreground">{selectedClient.name}</span>
            </>
          ) : (
            <span className="flex-1 text-left font-normal text-[14px] text-foreground/75">Select a client</span>
          )}
          <CaretDown size={14} className="text-muted-foreground ml-auto" />
        </button>
      </PopoverTrigger>
      <AnimatePresence onExitComplete={handleExitComplete}>
        {visible && (
          <PopoverContent className="w-72 p-2 overflow-visible rounded-2xl shadow-md bg-popover relative" align="start" sideOffset={10} forceMount asChild style={{ border: "0.5px solid var(--hairline)" }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -4 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
            >
              {/* Arrow pointing up */}
              <div
                className="absolute -top-[5px] left-6 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                style={{ borderLeft: "0.5px solid var(--hairline)", borderTop: "0.5px solid var(--hairline)" }}
              />

              {/* Search */}
              <div className="px-2 pt-2 pb-0.5">
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-[13px] text-foreground bg-transparent placeholder:text-muted-foreground placeholder:font-normal font-medium rounded-lg border-[0.5px] border-input focus:outline-none focus-visible:outline-none"
                    style={{ outline: "none", boxShadow: "none" }}
                    data-testid="client-selector-search"
                    autoFocus
                  />
                </div>
              </div>

              {/* Client list */}
              <div className="max-h-[260px] overflow-y-auto overflow-x-hidden pt-2 pb-1" data-testid="client-selector-list">
                {isLoading && (
                  <div className="p-3 space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-2.5 px-2 py-1.5 animate-pulse">
                        <div className="w-7 h-7 rounded-full bg-muted" />
                        <div className="h-3.5 bg-muted rounded w-24" />
                      </div>
                    ))}
                  </div>
                )}

                {error && (
                  <div className="p-4 text-center" data-testid="client-selector-error">
                    <p className="text-sm text-destructive mb-2">Failed to load clients</p>
                    <Button variant="outline" size="sm" onClick={() => refetch()}>
                      Retry
                    </Button>
                  </div>
                )}

                {!isLoading && !error && clients.length === 0 && (
                  <div className="p-4 text-center" data-testid="client-selector-empty">
                    <p className="text-sm text-muted-foreground mb-3">No clients yet</p>
                    <Button
                      variant="brand"
                      size="sm"
                      onClick={() => { setOpen(false); navigate({ to: "/clients" }); }}
                    >
                      <Users size={14} className="mr-1" />
                      Create Your First Client
                    </Button>
                  </div>
                )}

                {!isLoading && !error && filtered.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => { onSelectClient(c.id); setVisible(false); }}
                    className="flex items-stretch gap-2.5 w-[calc(100%-12px)] ml-1 rounded-lg px-2 py-2 text-[13px] text-left transition-colors cursor-pointer border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs"
                    data-testid={`client-option-${c.id}`}
                  >
                    <ClientAvatar name={c.name} logoUrl={c.logoUrl ?? null} size="sm" />
                    <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                      <span className="truncate">{c.name}</span>
                      {c.platforms && c.platforms.length > 0 && (
                        <div className="flex items-center flex-nowrap gap-1">
                          {c.platforms.slice(0, 7).map((platformId) => {
                            const platform = PICKER_PLATFORMS.find((p) => p.platform === platformId);
                            if (!platform?.domain) return null;
                            return (
                              <img
                                key={platformId}
                                src={logoUrl(platform.domain)}
                                alt={platform.displayName}
                                title={platform.displayName}
                                className="w-5 h-5 rounded-full bg-white ring-[1.5px] ring-white object-contain"
                                loading="lazy"
                              />
                            );
                          })}
                          {c.platforms.length > 7 && (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-background text-[10px] font-medium text-foreground border border-input shadow-xs">
                              +{c.platforms.length - 7}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {c.id === selectedClientId && (
                      <div className="self-start mr-[-3px] w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0">
                        <Check size={12} className="text-white" weight="bold" />
                      </div>
                    )}
                  </button>
                ))}

                {!isLoading && !error && clients.length > 0 && filtered.length === 0 && (
                  <p className="p-3 text-sm text-muted-foreground text-center">No clients match your search</p>
                )}
              </div>
            </motion.div>
          </PopoverContent>
        )}
      </AnimatePresence>
    </Popover>
  );
}

// ── Method cards (cloned from the report cards, dashboard wording) ───────────

function StartFromScratchDashboardCard({
  onStart,
  disabled = false,
}: {
  onStart: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onStart}
      disabled={disabled}
      data-testid="start-from-scratch-card"
      className={`relative overflow-hidden rounded-[30px] bg-card px-6 py-6 h-full w-full text-left shadow-sm cursor-pointer border-[0.5px] border-[var(--hairline)] ${
        disabled ? "opacity-50 pointer-events-none" : ""
      }`}
    >
      <div className="flex flex-col items-center text-center gap-3 h-full">
        <FileDashedIcon size={24} className="text-foreground" />
        <div>
          <h3 className="text-base font-semibold text-foreground">Start from Scratch</h3>
          <p className="text-sm text-foreground/70 mt-1">
            Begin with a blank canvas and design every section of your dashboard
            exactly the way you want it.
          </p>
        </div>
      </div>
    </button>
  );
}

function SmartDashboardCard({
  connectedPlatforms,
  onGenerate,
}: {
  connectedPlatforms: string[];
  onGenerate: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onGenerate}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onGenerate(); } }}
      data-testid="smart-dashboard-card"
      className="relative overflow-hidden rounded-[30px] bg-card px-6 py-6 h-full shadow-sm cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring border-[0.5px] border-[var(--hairline)]"
    >
      <div className="flex flex-col items-center text-center gap-3 h-full">
        <MagicWandIcon size={24} className="text-foreground" />
        <div>
          <h3 className="text-base font-semibold text-foreground">Generate Smart Dashboard</h3>
          <p className="text-sm text-foreground/70 mt-1">
            Auto-create a dashboard based on your connected platforms and key metrics
          </p>
        </div>
        {connectedPlatforms.length > 0 && (
          <div className="flex items-center justify-center flex-nowrap gap-1" data-testid="smart-dashboard-platforms">
            {connectedPlatforms.slice(0, 10).map((platformId) => {
              const platform = PICKER_PLATFORMS.find((p) => p.platform === platformId);
              if (!platform?.domain) return null;
              return (
                <img
                  key={platformId}
                  src={logoUrl(platform.domain)}
                  alt={platform.displayName}
                  title={platform.displayName}
                  className="w-5 h-5 rounded-full bg-white ring-[1.5px] ring-white object-contain"
                  loading="lazy"
                />
              );
            })}
            {connectedPlatforms.length > 10 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-background text-[10px] font-medium text-foreground border border-input shadow-xs">
                +{connectedPlatforms.length - 10}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AiDashboardCard() {
  return (
    <div
      data-testid="ai-dashboard-card"
      className="relative overflow-hidden rounded-[30px] bg-card px-6 py-6 h-full shadow-sm opacity-60 pointer-events-none border-[0.5px] border-[var(--hairline)]"
    >
      {/* Coming soon badge */}
      <span className="absolute top-3 right-3 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-muted text-muted-foreground">
        Coming soon
      </span>

      <div className="flex flex-col items-center text-center gap-3 h-full">
        <BrainIcon size={24} className="text-foreground" />
        <div>
          <h3 className="text-base font-semibold text-foreground">Create a Dashboard with AI</h3>
          <p className="text-sm text-foreground/70 mt-1">
            Connect Claude or ChatGPT, describe what you need, and your AI
            assistant will build the dashboard for you.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

function DashboardNewPage() {
  const { clientId, kind, rollupScope, brandId, members } = Route.useSearch();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();

  const [isGenerating, setIsGenerating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<"scratch" | "smart" | "ai" | null>(null);

  // Story 40-7 — roll-up vs single-client kind (search-param driven). Absent ⇒
  // the existing single-client "Choose Client" flow (byte-identical).
  const isRollup = kind === "rollup";
  const portfolioMemberIds = useMemo(() => parseMemberIds(members), [members]);

  // Client list — also used to default the selection to the first client when no
  // ?clientId= is present.
  const { data: clientsData } = useQuery<Client[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) throw new Error("Failed to fetch clients");
      const json = await res.json();
      return json.data as Client[];
    },
  });
  const activeClients = useMemo(
    () => (clientsData ?? []).filter((c) => !c.isArchived),
    [clientsData],
  );

  // Effective selection: ?clientId= wins, else the first client.
  const effectiveClientId = clientId ?? activeClients[0]?.id;
  const selectedClient = activeClients.find((c) => c.id === effectiveClientId) ?? null;
  const clientName = selectedClient?.name;

  const { templates } = useReportTemplates();
  const { connectedPlatforms } = useClientIntegrations(effectiveClientId);

  // Roll-up member resolution. Brand members are resolved DYNAMICALLY (never
  // frozen) from the brand's current clients; portfolio members come from the URL
  // member set. The brand query is idle for a single-client / portfolio flow.
  const { data: rollupBrand } = useBrand(
    isRollup && rollupScope === "brand" ? brandId : undefined,
  );
  const rollupMemberIds = useMemo<string[]>(() => {
    if (!isRollup) return [];
    if (rollupScope === "brand") {
      return (rollupBrand?.clients ?? []).map((c) => c.id);
    }
    return portfolioMemberIds;
  }, [isRollup, rollupScope, rollupBrand, portfolioMemberIds]);

  // ── Single-client create (byte-identical to today) ─────────────────────
  const createDashboard = useCallback(
    async (name: string, widgets: import("../../../lib/templateUtils").WidgetConfig[]) => {
      if (!effectiveClientId || creating) return;
      setCreating(true);
      setIsGenerating(true);
      try {
        const res = await apiPost("/api/dashboards", {
          clientId: effectiveClientId,
          name,
          canvasLayout: toCanvasLayout(widgets),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.detail ?? err?.title ?? "Failed to create dashboard");
        }
        const json = (await res.json().catch(() => null)) as {
          data?: { id?: string };
        } | null;
        const newId = json?.data?.id;
        if (!newId) throw new Error("Create returned no id");
        navigate({
          to: "/dashboard/$dashboardId/edit",
          params: { dashboardId: newId },
        });
      } catch (err) {
        showToast("error", err instanceof Error ? err.message : "Failed to create dashboard");
        setCreating(false);
        setIsGenerating(false);
      }
    },
    [effectiveClientId, creating, navigate, showToast],
  );

  const handleStartFromScratch = useCallback(() => {
    void createDashboard("Untitled Dashboard", []);
  }, [createDashboard]);

  // Smart dashboard: Full Marketing template filtered to the client's connected
  // platforms — the same widget-seed the report Smart flow uses (importable lib).
  const handleSmartDashboard = useCallback(() => {
    const fullMarketing = templates.find((t) => t.id === FULL_MARKETING_TEMPLATE_ID);
    const widgets = fullMarketing
      ? filterWidgetsByAvailablePlatforms(
          fullMarketing.parsedLayout.widgets,
          connectedPlatforms,
        )
      : [];
    void createDashboard("Smart Dashboard", widgets);
  }, [templates, connectedPlatforms, createDashboard]);

  // ── Roll-up create (Story 40-7) — NO clientId; kind/scope/member set ────
  // The "Generate Smart Dashboard" template-filter path is single-client-only; a
  // roll-up offers "Start from Scratch" — an empty canvas the user fills with
  // aggregate widgets. The server discriminator (40-7) validates the shape.
  const handleCreateRollup = useCallback(async () => {
    if (creating) return;
    if (rollupMemberIds.length === 0 || rollupMemberIds.length > ROLLUP_MEMBER_CAP) return;
    setCreating(true);
    setIsGenerating(true);
    try {
      const name =
        rollupScope === "brand"
          ? `${rollupBrand?.name ?? "Brand"} roll-up`
          : `Portfolio roll-up (${rollupMemberIds.length} clients)`;
      // Roll-up payload: NO clientId; kind + scope + member set (brandId for
      // brand scope, memberClientIds for portfolio). canvasLayout is an empty
      // start-from-scratch canvas.
      const rollupPayload =
        rollupScope === "brand"
          ? { kind: "rollup" as const, rollupScope: "brand" as const, brandId }
          : {
              kind: "rollup" as const,
              rollupScope: "portfolio" as const,
              memberClientIds: rollupMemberIds,
            };
      const res = await apiPost("/api/dashboards", {
        ...rollupPayload,
        name,
        canvasLayout: toCanvasLayout([]),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail ?? err?.title ?? "Failed to create dashboard");
      }
      const json = (await res.json().catch(() => null)) as {
        data?: { id?: string };
      } | null;
      const newId = json?.data?.id;
      if (!newId) throw new Error("Create returned no id");
      navigate({
        to: "/dashboard/$dashboardId/edit",
        params: { dashboardId: newId },
      });
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Failed to create dashboard");
      setCreating(false);
      setIsGenerating(false);
    }
  }, [creating, rollupMemberIds, rollupScope, rollupBrand, brandId, navigate, showToast]);

  const handleSelectClient = useCallback(
    (id: string) => {
      navigate({ to: "/dashboard/new", search: { clientId: id } });
    },
    [navigate],
  );

  // ── Type-picker (Story 40-7) — kind / scope / member selection ─────────
  // Each selection is persisted in the URL search params (deep-link safe).
  const handleSelectKind = useCallback(
    (k: "client" | "rollup") => {
      navigate({ to: "/dashboard/new", search: { kind: k } });
    },
    [navigate],
  );
  const handleSelectScope = useCallback(
    (scope: "brand" | "portfolio") => {
      navigate({ to: "/dashboard/new", search: { kind: "rollup", rollupScope: scope } });
    },
    [navigate],
  );
  const handleSelectBrand = useCallback(
    (bId: string) => {
      navigate({
        to: "/dashboard/new",
        search: { kind: "rollup", rollupScope: "brand", brandId: bId },
      });
    },
    [navigate],
  );
  const handlePortfolioChange = useCallback(
    (ids: string[]) => {
      navigate({
        to: "/dashboard/new",
        search: {
          kind: "rollup",
          rollupScope: "portfolio",
          ...(ids.length > 0 ? { members: ids.join(",") } : {}),
        },
      });
    },
    [navigate],
  );

  // A roll-up can be created once it has a non-empty resolved member set within
  // the cap (brand with ≥1 member, or ≥1 portfolio client).
  const canCreateRollup =
    rollupMemberIds.length > 0 && rollupMemberIds.length <= ROLLUP_MEMBER_CAP;

  const breadcrumbItems = useMemo(
    () => [
      { label: "Dashboard", to: "/dashboard" },
      {
        label: isRollup
          ? "New Roll-up Dashboard"
          : clientName
            ? `New Dashboard for ${clientName}`
            : "New Dashboard",
      },
    ],
    [isRollup, clientName],
  );

  if (isGenerating) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-6">
          <div className="w-14 h-14 border-[6px] border-muted border-t-primary rounded-full animate-spin" />
          <p className="text-base font-medium text-muted-foreground animate-pulse">
            Creating your dashboard...
          </p>
        </div>
      </div>
    );
  }

  const hasClient = !!effectiveClientId;

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      <ReportBreadcrumb items={breadcrumbItems} />

      <div className="max-w-5xl mx-auto">
        {/* Type-picker (Story 40-7) — Client vs Roll-up, and for a roll-up its
            scope (Brand vs Portfolio) + member set. Reused from the report wizard
            (40-6). The single-client flow below is unchanged. */}
        <RollupSetupStep
          kind={kind ?? null}
          rollupScope={rollupScope ?? null}
          brandId={brandId ?? null}
          memberClientIds={portfolioMemberIds}
          onSelectKind={handleSelectKind}
          onSelectScope={handleSelectScope}
          onSelectBrand={handleSelectBrand}
          onPortfolioChange={handlePortfolioChange}
        />

        {/* ── Roll-up create CTA (Story 40-7) ────────────────────────────── */}
        {isRollup && (
          <div className="mt-8 flex flex-col items-center gap-2" data-testid="rollup-create-panel">
            <Button
              variant="brand"
              disabled={!canCreateRollup || creating}
              onClick={handleCreateRollup}
              data-testid="rollup-create-button"
              className="rounded-2xl px-6"
            >
              Start from Scratch
            </Button>
            <p className="text-[12px] text-foreground/60">
              Begin with a blank canvas and add aggregate widgets across{" "}
              {rollupMemberIds.length > 0
                ? `${rollupMemberIds.length} ${rollupMemberIds.length === 1 ? "client" : "clients"}`
                : "your clients"}
              .
            </p>
          </div>
        )}
      </div>

      {/* ── Single-client "Choose Client" flow (UNCHANGED) — hidden once the
          Roll-up kind is chosen. Absent/"client" kind keeps the existing flow,
          so a direct /dashboard/new entry is byte-identical to today. ── */}
      {!isRollup && (
      <div className="max-w-5xl mx-auto mt-10">
        {/* Heading */}
        <div className="text-center mb-6">
          <h2 className="text-[21px] font-semibold tracking-tight">Choose Client for Dashboard</h2>
          <p className="text-[15px] text-foreground/70 mt-1">Select the client for this dashboard</p>
        </div>

        {/* Centered client selector */}
        <div className="flex flex-col items-center mb-6">
          <ClientSelectorPopover
            selectedClientId={effectiveClientId}
            selectedClient={selectedClient}
            onSelectClient={handleSelectClient}
          />
        </div>

        {/* Forking connector bus — 3 L-shaped branches to the centers of the cards */}
        {(() => {
          const dimColor = "text-muted-foreground/40";
          const activeHover = hasClient ? hoveredCard : null;
          const stemColor = activeHover ? "text-primary" : dimColor;
          const busColor = dimColor;
          const dropClass = (slot: "scratch" | "smart" | "ai") =>
            `flex justify-center transition-colors duration-200 ${
              activeHover === slot ? "text-primary" : dimColor
            }`;
          const overlayStyle: React.CSSProperties | null =
            activeHover === "scratch"
              ? { left: "15.9%", right: "50%" }
              : activeHover === "ai"
                ? { left: "50%", right: "15.9%" }
                : null;
          return (
            <div className="-mt-6 mb-1" aria-hidden>
              {/* Stem */}
              <div className={`flex justify-center transition-colors duration-200 ${stemColor}`}>
                <svg width="4" height="36" viewBox="0 0 4 36" className="overflow-visible" style={{ transform: "translateX(-0.5px)" }}>
                  <line x1="2" y1="0" x2="2" y2="36" stroke="currentColor" strokeWidth="1.5" strokeLinecap="butt" />
                </svg>
              </div>
              {/* Drops + horizontal bus */}
              <div className="grid grid-cols-3 gap-6 relative">
                <div
                  className={`absolute top-0 h-[1.5px] rounded-full transition-colors duration-200 ${busColor}`}
                  style={{ left: "15.9%", right: "15.9%", backgroundColor: "currentColor" }}
                />
                {overlayStyle && (
                  <div
                    className="absolute top-0 h-[1.5px] rounded-full bg-current text-primary transition-colors duration-200"
                    style={overlayStyle}
                  />
                )}
                {(["scratch", "smart", "ai"] as const).map((slot) => (
                  <div key={slot} className={dropClass(slot)}>
                    <svg width="14" height="55" viewBox="0 0 14 55" className="overflow-visible">
                      <line x1="7" y1="0" x2="7" y2="52" stroke="currentColor" strokeWidth="1.5" strokeLinecap="butt" />
                      <polyline points="1,47 7,53 13,47" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Three method cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {/* Start from Scratch */}
          <div
            onMouseEnter={() => setHoveredCard("scratch")}
            onMouseLeave={() => setHoveredCard(null)}
            className={`transition-all duration-200 rounded-[30px] ${hasClient ? "group/card hover:-translate-y-0.5 hover:shadow-sm" : ""}`}
          >
            <StartFromScratchDashboardCard onStart={handleStartFromScratch} disabled={!hasClient} />
          </div>

          {/* Generate Smart Dashboard */}
          <div
            onMouseEnter={() => setHoveredCard("smart")}
            onMouseLeave={() => setHoveredCard(null)}
            className={`transition-all duration-200 rounded-[30px] ${hasClient ? "group/card hover:-translate-y-0.5 hover:shadow-sm" : ""}`}
          >
            <AnimatePresence>
              {hasClient && connectedPlatforms.length > 0 && (
                <motion.div
                  key="smart-dashboard"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <SmartDashboardCard
                    connectedPlatforms={connectedPlatforms}
                    onGenerate={handleSmartDashboard}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            {(!hasClient || connectedPlatforms.length === 0) && (
              <div className="opacity-50 pointer-events-none">
                <SmartDashboardCard
                  connectedPlatforms={["google-analytics-4", "facebook-ads", "instagram-business", "linkedin-pages"]}
                  onGenerate={() => {}}
                />
              </div>
            )}
          </div>

          {/* Create with AI (coming soon) */}
          <div
            onMouseEnter={() => setHoveredCard("ai")}
            onMouseLeave={() => setHoveredCard(null)}
            className={`transition-all duration-200 rounded-[30px] ${hasClient ? "group/card hover:-translate-y-0.5 hover:shadow-sm" : ""}`}
          >
            <AiDashboardCard />
          </div>
        </div>
      </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
